import { Clock3, Mail, MessageSquareText, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitContact } from '../lib/api';
import { Seo } from '../lib/seo';

const initialState = {
  name: '',
  email: '',
  subject: '',
  category: 'General question',
  orderReference: '',
  message: ''
};

export function ContactPage() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await submitContact(form);
      setSuccess('Thanks — your message was sent successfully. Our support team normally replies within 1–2 business days.');
      setForm(initialState);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="page-section contact-page">
      <Seo
        title="Contact Nexhance AI Support"
        description="Contact Nexhance AI for AI image enhancement support, billing questions, plan help, refund requests and technical issues."
        path="/contact"
        noindex
      />
      <div className="shell">
        <div className="page-hero compact-page-hero">
          <p className="eyebrow">Contact support</p>
          <h1>Get help with image processing, credits, billing or account questions.</h1>
          <p>If you need help choosing the right image tool, reporting a failed job, asking about billing, or requesting general support, send a message here and include enough detail for a faster reply.</p>
        </div>

        <div className="contact-grid">
          <article className="contact-info-card">
            <span className="contact-icon"><Mail size={20} /></span>
            <h2>Support desk</h2>
            <p>Send your request through the secure form. It is delivered directly to the Nexhance AI support inbox without exposing a private email address publicly.</p>
            <div className="contact-points">
              <div><Clock3 size={16} /><span>Typical reply time: 1–2 business days.</span></div>
              <div><MessageSquareText size={16} /><span>Useful for technical issues, billing questions, plan help and general support.</span></div>
              <div><ShieldCheck size={16} /><span>Do not include passwords, payment-card numbers or highly sensitive private information.</span></div>
            </div>
          </article>

          <form className="contact-form panel" onSubmit={onSubmit}>
            <div className="contact-form-grid">
              <label>
                <span>Your name</span>
                <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required minLength={2} placeholder="Your full name" />
              </label>
              <label>
                <span>Email address</span>
                <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} required placeholder="you@example.com" />
              </label>
              <label>
                <span>Subject</span>
                <input value={form.subject} onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))} required minLength={3} placeholder="What do you need help with?" />
              </label>
              <label>
                <span>Category</span>
                <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}>
                  <option>General question</option>
                  <option>Technical support</option>
                  <option>Billing and plans</option>
                  <option>Refund request</option>
                  <option>Account issue</option>
                  <option>Privacy or data request</option>
                  <option>Copyright or abuse report</option>
                  <option>Content correction</option>
                  <option>Feature suggestion</option>
                </select>
              </label>
              <label className="contact-form-full">
                <span>Order or account reference (optional)</span>
                <input value={form.orderReference} onChange={(event) => setForm((current) => ({ ...current, orderReference: event.target.value }))} placeholder="Subscription ID, payment email, or anything useful" />
              </label>
              <label className="contact-form-full">
                <span>Message</span>
                <textarea rows={7} value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} required minLength={5} placeholder="Describe the issue, include the tool you used, what happened, and what you expected." />
              <small className="contact-character-count">{form.message.trim().length}/5000 characters · minimum 5</small>
              </label>
            </div>
            {error && <div className="inline-error">{error}</div>}
            {success && <div className="inline-success">{success}</div>}
            <div className="contact-submit-row">
              <button className="button button-primary button-large" type="submit" disabled={submitting}>{submitting ? 'Sending message…' : 'Send message'}</button>
              <p>Support normally replies within 1–2 business days. Include the tool, selected settings and any error message for a faster answer.</p>
            </div>
          </form>
        </div>

        <section className="contact-seo-copy">
          <div className="section-heading centered">
            <p className="eyebrow">Support topics</p>
            <h2>What you can contact Nexhance AI about</h2>
            <p>Support covers both the credit-based AI studio and the separate free image utilities, so include the exact tool name whenever the issue depends on a specific workflow.</p>
          </div>
          <div className="contact-topic-grid">
            <article><h3>AI image enhancement support</h3><p>Ask which workflow fits blurry, noisy, low-resolution or damaged photos before spending credits. Include whether the problem is blur, grain, compression, poor lighting or missing resolution.</p></article>
            <article><h3>Generative image editing</h3><p>Get help with object removal, text removal, AI background changes, image extension or prompt-based edits when the model changes more of the scene than expected.</p></article>
            <article><h3>Free image utility help</h3><p>Report browser issues with image compression, resizing, cropping, format conversion, social media presets, color tools or metadata-clean exports. These utilities use 0 credits.</p></article>
            <article><h3>Upscaling and quality questions</h3><p>Get guidance on when to choose 2×, 4× or 8× output and how to review faces, text, logos and repeating patterns before downloading.</p></article>
            <article><h3>Billing and plan help</h3><p>Contact us about plan selection, renewal questions, checkout issues, invoices, failed-job credit restoration, refunds and subscription management.</p></article>
            <article><h3>Privacy and data requests</h3><p>Ask about account data, AI uploads, local browser processing, temporary results, access or deletion requests. Do not send sensitive images through the form unless support specifically requests them.</p></article>
            <article><h3>Copyright or abuse reports</h3><p>Report suspected misuse, rights concerns or content that may violate the Acceptable Use Policy. Include the relevant URL, tool or account reference when available.</p></article>
            <article><h3>Content corrections</h3><p>Report a broken guide, outdated image-size recommendation or technical explanation that needs review. Include the page URL and exact section so the issue can be checked quickly.</p></article>
          </div>
        </section>

        <section className="contact-help-section">
          <div className="section-heading split-heading">
            <div><p className="eyebrow">Before you send a ticket</p><h2>A few details can turn a vague image problem into a useful support request.</h2></div>
            <p>You do not need to send the image itself for an initial question. Describe the source, tool, settings and visible problem first. If support later needs a sample, send only material you are comfortable sharing and have the right to provide.</p>
          </div>
          <div className="contact-help-grid">
            <article><span>01</span><h3>Name the exact tool</h3><p>For example: AI Deblur, Object Remover, Image Extender, Free Image Compressor or Social Media Image Resizer. Similar-looking workflows can fail for very different reasons.</p></article>
            <article><span>02</span><h3>Describe the source</h3><p>Include format, approximate dimensions and whether the file came from a camera, scan, screenshot, messaging app, marketplace download or previous AI generation.</p></article>
            <article><span>03</span><h3>Include the setting or prompt</h3><p>For AI editing, copy the instruction you used. For upscaling, mention 2×, 4× or 8×. For free utilities, include the chosen output format, dimensions or quality setting.</p></article>
            <article><span>04</span><h3>Say what looked wrong</h3><p>“It failed” is harder to diagnose than “the text changed,” “hair disappeared,” “the background repeated,” “the file stayed too large” or “the browser did not download the export.”</p></article>
          </div>
        </section>

        <section className="contact-resource-section">
          <div><p className="eyebrow">You may find the answer immediately</p><h2>Use the help pages when the question is about workflow, credits or policy rather than an account-specific problem.</h2></div>
          <div className="contact-resource-links">
            <Link to="/faq"><strong>FAQ</strong><span>AI tools, free utilities, image quality, privacy, formats and troubleshooting.</span></Link>
            <Link to="/plans-and-credits"><strong>Plans & credits</strong><span>How credit estimates, renewals, free utilities and failed-job restoration work.</span></Link>
            <Link to="/guides"><strong>Image guides</strong><span>Tool-by-tool preparation, quality checks, comparisons and export decisions.</span></Link>
            <Link to="/privacy"><strong>Privacy policy</strong><span>Account data, AI uploads, browser-side utilities, providers and retention.</span></Link>
            <Link to="/refund-policy"><strong>Refund policy</strong><span>Failed processing, duplicate charges, cancellations and refund review.</span></Link>
            <Link to="/acceptable-use"><strong>Acceptable use</strong><span>Rights, consent, identity misuse, harmful editing and platform abuse.</span></Link>
          </div>
        </section>
      </div>
    </section>
  );
}
